/**
 * Master Boot Sequence — Final Synchronization Layer
 *
 * Guarantees production render timing:
 *   1. API data ready
 *   2. Images preloaded
 *   3. Fonts loaded
 *   4. DOM flushed (Vue $nextTick)
 *   5. Double RAF barrier
 *   6. Swiper hard-sync
 *   7. Render unlocked
 */

import { preloadImagesBatched } from './image-preload'
import { waitForFonts } from './font-lock'
import { hardSync } from './swiper-sync'

/**
 * Full boot sequence for a component with Swiper.
 *
 * Usage in Vue component:
 *   async mounted() {
 *     const sync = await bootSequence({
 *       fetchData: () => fetchHomeData(),
 *       extractImages: (data) => [].concat(
 *         data.banners.map(b => b.url),
 *         data.partners.map(p => p.url),
 *       ),
 *       getSwipers: () => [this.bannerSwiper, this.partnerSwiper],
 *       onImageProgress: (pct) => { this.loadProgress = pct },
 *     })
 *     this.data = sync.data
 *     this.ready = true
 *   }
 */
export async function bootSequence({
  fetchData,        // async () => data
  extractImages,    // (data) => string[]
  getSwipers,       // () => Swiper[]
  onImageProgress,  // (pct: number) => void
  imageTimeout = 10000,
  fontTimeout = 5000,
} = {}) {
  const t0 = performance.now()
  const timeline = {}

  // 1. Fetch API data
  const data = await fetchData()
  timeline.apiReady = (performance.now() - t0).toFixed(0) + 'ms'

  // 2. Preload images (runs in background)
  const urls = extractImages ? extractImages(data) : []
  const imagePromise = preloadImagesBatched(urls, 6, imageTimeout)
  if (onImageProgress) {
    imagePromise.then(r => onImageProgress(r.loaded / r.total * 100))
  }

  // 3. Wait for fonts (parallel with image preload)
  const fontPromise = waitForFonts(fontTimeout)

  // 4. Wait for both image and font
  const [imgResult] = await Promise.all([imagePromise, fontPromise])
  timeline.imagesLoaded = (performance.now() - t0).toFixed(0) + 'ms'
  timeline.fontsReady = (performance.now() - t0).toFixed(0) + 'ms'

  // 5. DOM flush
  await new Promise(r => requestAnimationFrame(r))
  timeline.domFlush = (performance.now() - t0).toFixed(0) + 'ms'

  // 6. Swiper hard-sync
  const swipers = getSwipers ? getSwipers() : []
  if (swipers.length) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        swipers.forEach(s => { if (s) hardSync(s) })
      })
    })
  }
  timeline.swiperSynced = (performance.now() - t0).toFixed(0) + 'ms'

  timeline.total = (performance.now() - t0).toFixed(0) + 'ms'

  if (process.env.NODE_ENV === 'development') {
    console.log('[sync] Boot sequence complete:', timeline)
  }

  return {
    data,
    images: imgResult,
    timeline,
  }
}

/**
 * Minimal boot — just wait for data + DOM flush + double RAF.
 * For components without Swiper.
 */
export async function bootSimple(fetchData) {
  const data = await fetchData()
  await new Promise(r => requestAnimationFrame(r))
  return data
}
